import { useCallback, useEffect, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { BaseService, ServiceOptions } from './base-service';
import type { Id } from './base-service';
import {
    buildFieldErrorsFromElements,
    focusFirstInvalid,
    REQUIRED_FIELD_MESSAGE,
    resolveFormFromEvent,
    validateRequiredFields,
} from './form-required-validation';
import { resolveApiErrorParts } from './api-error-helpers';

// Sentinel used to indicate that a record was not found (HTTP 404) when loading for edit.
export const FORM_NOT_FOUND_ERROR = '__FORM_RECORD_NOT_FOUND__';
// Sentinel used to indicate the form is still loading an edit record (used to avoid UI flicker).
export const FORM_LOADING_ERROR = '__FORM_LOADING__';

/**
 * Form state interface
 */
export interface FormState<T> {
    data: T;
    loading: boolean;
    error: FormError;
    isDirty: boolean;
    isValid: boolean;
    touched: Set<keyof T>;
    errors: Partial<Record<keyof T, string>>;
}

export type FormError = string | { message: string; details?: string[] } | Error | null;

/**
 * Validation rule
 */
export type ValidationRule<T, K extends keyof T> = {
    validate: (value: T[K], formData: T) => boolean | string;
    message?: string;
};

/**
 * Validation rules for form fields
 */
export type ValidationRules<T> = {
    [K in keyof T]?: ValidationRule<T, K>[];
};

export interface UseFormResult<TRecord, TFormData> {
    formData: TFormData;
    formState: FormState<TFormData>;
    handleChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
    handleSubmit: (event?: FormEvent<HTMLFormElement>) => Promise<TRecord>;
    loading: boolean;
    submitting: boolean;
    error: FormError;
    resetForm: () => void;
    setFieldValue: <K extends keyof TFormData>(field: K, value: TFormData[K]) => void;
    setFieldsValue: (updates: Partial<TFormData>) => void;
}

/**
 * Form service options
 */
type UpdateTransformer<TFormData, TUpdateDto> =
    | ((formData: Partial<TFormData>, id: string | number) => TUpdateDto | Promise<TUpdateDto>)
    | ((formData: TFormData, id: string | number) => TUpdateDto | Promise<TUpdateDto>);

export interface FormServiceOptions<TRecord, TFormData, TCreateDto, TUpdateDto> extends ServiceOptions<TRecord> {
    /**
     * Initial form data
     */
    initialData?: Partial<TFormData>;

    /**
     * Validation rules for form fields
     */
    validationRules?: ValidationRules<TFormData>;

    /**
     * Transform form data before submit (create)
     */
    transformForCreate?: (formData: TFormData) => TCreateDto | Promise<TCreateDto>;

    /**
     * Transform form data before submit (update)
        */
        transformForUpdate?: UpdateTransformer<TFormData, TUpdateDto>;

    /**
     * Transform record from API to form data
     */
    transformToForm?: (record: TRecord) => TFormData;
}

export class FormValidationError extends Error {
    isValidationError = true;
    fieldErrors: Partial<Record<string, string>> | null;
    formData: unknown;

    constructor(message = 'Existem campos inválidos. Revise os destaques e tente novamente.') {
        super(message);
        this.name = 'FormValidationError';
        this.fieldErrors = null;
        this.formData = null;
    }
}

export class BaseFormService<
    TRecord = any,
    TFormData = any,
    TCreateDto = TFormData,
    TUpdateDto = Partial<TCreateDto>,
    TFilters = Record<string, any>,
> extends BaseService<TRecord, TCreateDto, TUpdateDto, TFilters> {
    protected readonly initialData: Partial<TFormData>;
    protected readonly validationRules: ValidationRules<TFormData>;
    protected readonly transformForCreate?: (formData: TFormData) => TCreateDto | Promise<TCreateDto>;
    protected readonly transformForUpdate?: UpdateTransformer<TFormData, TUpdateDto>;
    protected readonly transformToForm?: (record: TRecord) => TFormData;

    constructor(options: FormServiceOptions<TRecord, TFormData, TCreateDto, TUpdateDto>) {
        super(options);
        this.initialData = options.initialData || ({} as Partial<TFormData>);
        this.validationRules = options.validationRules || {};
        this.transformForCreate = options.transformForCreate;
        this.transformForUpdate = options.transformForUpdate;
        this.transformToForm = options.transformToForm;
    }

    /**
     * Initialize form state with initial data
     */
    initFormState(data?: Partial<TFormData>): FormState<TFormData> {
        const initialFormData = { ...this.initialData, ...data } as TFormData;

        return {
            data: initialFormData,
            loading: false,
            error: null,
            isDirty: false,
            isValid: true,
            touched: new Set<keyof TFormData>(),
            errors: {},
        };
    }

    /**
     * Load record and convert to form state
     */
    async loadForEdit(id: string | number): Promise<FormState<TFormData>> {
        const formState = this.initFormState();
        formState.loading = true;

        try {
            const record = await this.get(id);
            const formData = this.transformToForm ? this.transformToForm(record) : ((record as unknown) as TFormData);

            formState.data = formData;
            formState.loading = false;
            formState.error = null;
        } catch (error: any) {
            formState.loading = false;
            const status = error?.status ?? error?.response?.status ?? error?.cause?.status;
            // Redirect flow expects the sentinel whenever loading fails for an edit path.
            formState.error = status === 404 ? FORM_NOT_FOUND_ERROR : FORM_NOT_FOUND_ERROR;
        }

        return formState;
    }

    /**
     * Update a single field in form state
     */
    updateField<K extends keyof TFormData>(formState: FormState<TFormData>, field: K, value: TFormData[K]): void {
        formState.data[field] = value;
        formState.isDirty = true;
        formState.touched.add(field);

        // Validate field
        this.validateField(formState, field);
    }

    /**
     * Update multiple fields
     */
    updateFields(formState: FormState<TFormData>, updates: Partial<TFormData>): void {
        Object.entries(updates).forEach(([key, value]) => {
            const field = key as keyof TFormData;
            formState.data[field] = value as TFormData[typeof field];
            formState.touched.add(field);
        });

        formState.isDirty = true;
        this.validate(formState);
    }

    /**
     * Validate a single field
     */
    validateField<K extends keyof TFormData>(formState: FormState<TFormData>, field: K): boolean {
        const rules = this.validationRules[field];

        if (!rules || rules.length === 0) {
            delete formState.errors[field];
            return true;
        }

        for (const rule of rules) {
            const result = rule.validate(formState.data[field], formState.data);

            if (result === false || typeof result === 'string') {
                formState.errors[field] = typeof result === 'string' ? result : rule.message || 'Invalid value';
                // eslint-disable-next-line no-console
                console.debug('BaseFormService.validateField failed', field, 'value:', formState.data[field], 'error:', formState.errors[field]);
                return false;
            }
        }

        delete formState.errors[field];
        return true;
    }

    /**
     * Validate entire form
     */
    validate(formState: FormState<TFormData>): boolean {
        const fields = Object.keys(this.validationRules) as Array<keyof TFormData>;

        let isValid = true;
        for (const field of fields) {
            const fieldValid = this.validateField(formState, field);
            if (!fieldValid) {
                isValid = false;
                // eslint-disable-next-line no-console
                console.debug('BaseFormService.validate field failed', field);
            }
        }

        formState.isValid = isValid;
        return isValid;
    }

    /**
     * Reset form to initial state
     */
    reset(formState: FormState<TFormData>): void {
        formState.data = { ...this.initialData } as TFormData;
        formState.isDirty = false;
        formState.isValid = true;
        formState.touched.clear();
        formState.errors = {};
        formState.error = null;
    }

    /**
     * Submit form for creating new record
     */
    async submitCreate(formData: TFormData): Promise<TRecord> {
        const transformedData = this.transformForCreate
            ? await this.transformForCreate(formData)
            : ((formData as unknown) as TCreateDto);

        return this.create(transformedData);
    }

    /**
     * Submit form for updating existing record
     */
    async submitUpdate(id: string | number, formData: Partial<TFormData>): Promise<TRecord> {
        const transformedData = this.transformForUpdate
            ? await (this.transformForUpdate as (formData: any, id: string | number) => TUpdateDto)(formData, id)
            : ((formData as unknown) as TUpdateDto);

        return this.update(id, transformedData);
    }

    useForm(id?: Id): UseFormResult<TRecord, TFormData> {
        return useBaseFormHook(this, id);
    }

    /**
     * Check if field has been touched
     */
    isTouched<K extends keyof TFormData>(formState: FormState<TFormData>, field: K): boolean {
        return formState.touched.has(field);
    }

    /**
     * Check if field has error
     */
    hasError<K extends keyof TFormData>(formState: FormState<TFormData>, field: K): boolean {
        return !!formState.errors[field];
    }

    /**
     * Get field error message
     */
    getError<K extends keyof TFormData>(formState: FormState<TFormData>, field: K): string | undefined {
        return formState.errors[field];
    }

    /**
     * Check if form can be submitted
     */
    canSubmit(formState: FormState<TFormData>): boolean {
        return formState.isDirty && formState.isValid && !formState.loading;
    }
}

/**
 * Common validation rules
 */
export const ValidationRules = {
    required: <T, K extends keyof T>(message = REQUIRED_FIELD_MESSAGE): ValidationRule<T, K> => ({
        validate: (value) => {
            if (value === null || value === undefined || value === '') return false;
            if (Array.isArray(value) && value.length === 0) return false;
            return true;
        },
        message,
    }),

    minLength: <T, K extends keyof T>(min: number, message?: string): ValidationRule<T, K> => ({
        validate: (value) => {
            if (typeof value !== 'string') return true;
            return value.length >= min;
        },
        message: message || `Minimum ${min} characters required`,
    }),

    maxLength: <T, K extends keyof T>(max: number, message?: string): ValidationRule<T, K> => ({
        validate: (value) => {
            if (typeof value !== 'string') return true;
            return value.length <= max;
        },
        message: message || `Maximum ${max} characters allowed`,
    }),

    email: <T, K extends keyof T>(message = 'Invalid email address'): ValidationRule<T, K> => ({
        validate: (value) => {
            if (typeof value !== 'string') return true;
            if (value === '') return true; // Let required rule handle empty
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
        },
        message,
    }),

    min: <T, K extends keyof T>(min: number, message?: string): ValidationRule<T, K> => ({
        validate: (value) => {
            if (typeof value !== 'number') return true;
            return value >= min;
        },
        message: message || `Minimum value is ${min}`,
    }),

    max: <T, K extends keyof T>(max: number, message?: string): ValidationRule<T, K> => ({
        validate: (value) => {
            if (typeof value !== 'number') return true;
            return value <= max;
        },
        message: message || `Maximum value is ${max}`,
    }),

    pattern: <T, K extends keyof T>(regex: RegExp, message = 'Invalid format'): ValidationRule<T, K> => ({
        validate: (value) => {
            if (typeof value !== 'string') return true;
            if (value === '') return true;
            return regex.test(value);
        },
        message,
    }),

    custom: <T, K extends keyof T>(validator: (value: T[K], formData: T) => boolean | string, message?: string): ValidationRule<T, K> => ({
        validate: validator,
        message,
    }),
};

function useBaseFormHook<
    TRecord = any,
    TFormData = any,
    TCreateDto = TFormData,
    TUpdateDto = Partial<TCreateDto>,
    TFilters = Record<string, any>,
>(service: BaseFormService<TRecord, TFormData, TCreateDto, TUpdateDto, TFilters>, id?: Id): UseFormResult<TRecord, TFormData> {
    const [formState, setFormState] = useState<FormState<TFormData>>(() => {
        const initial = service.initFormState();
        if (id !== undefined && id !== null) {
            initial.loading = true;
            initial.error = FORM_LOADING_ERROR;
        }
        return initial;
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<FormError>(id ? FORM_LOADING_ERROR : null);

    useEffect(() => {
        let mounted = true;

        if (!id) {
            setFormState(service.initFormState());
            return () => {
                mounted = false;
            };
        }

        (async () => {
            try {
                const loadedState = await service.loadForEdit(id);
                if (!mounted) return;
                setFormState(loadedState);
                setError(null);
            } catch (err: any) {
                if (!mounted) return;
                setError(err ?? new Error('Failed to load record'));
            }
        })();

        return () => {
            mounted = false;
        };
    }, [id, service]);

    const setFieldValue = useCallback(<K extends keyof TFormData>(field: K, value: TFormData[K]) => {
        setFormState((current) => {
            const nextState = cloneFormState(current);
            service.updateField(nextState, field, value);
            return nextState;
        });
    }, [service]);

    const setFieldsValue = useCallback((updates: Partial<TFormData>) => {
        setFormState((current) => {
            const nextState = cloneFormState(current);
            service.updateFields(nextState, updates);
            return nextState;
        });
    }, [service]);

    const handleChange = useCallback(
        (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
            const { name } = event.target;
            const value = extractInputValue(event);
            setFieldValue(name as keyof TFormData, value as TFormData[keyof TFormData]);
        },
        [setFieldValue],
    );

    const handleSubmit = useCallback(
        async (event?: FormEvent<HTMLFormElement>) => {
            if (event) event.preventDefault();
            setError(null);

            const formElement = resolveFormFromEvent(event);
            const validation = validateRequiredFields(formElement);
            if (!validation.isValid) {
                const validationError = new FormValidationError(REQUIRED_FIELD_MESSAGE);
                validationError.fieldErrors = buildFieldErrorsFromElements(validation.invalidElements);
                validationError.formData = cloneFormState(formState).data;
                  setError(validationError);
                focusFirstInvalid(validation.invalidElements);
                throw validationError;
            }

            setSubmitting(true);

              try {
                  const payload = cloneFormState(formState).data;
                  const record = id ? await service.submitUpdate(id, payload) : await service.submitCreate(payload as TFormData);

                  // Refresh form state with the saved record to reflect updated audit fields
                  setFormState(() => {
                      const toForm = (service as any).transformToForm as
                          | ((record: TRecord) => TFormData)
                          | undefined;
                      const formData = toForm ? toForm(record) : ((record as unknown) as TFormData);
                      return service.initFormState(formData);
                  });

                  return record;
              } catch (err: any) {
                  const parts = resolveApiErrorParts(err, 'Não foi possível enviar os dados. Tente novamente.');
                  setError(parts);
                  throw err;
            } finally {
                setSubmitting(false);
            }
        },
        [formState, id, service],
    );

    const resetForm = useCallback(() => {
        setFormState(service.initFormState());
        setError(null);
    }, [service]);

    return {
        formData: formState.data,
        formState,
        handleChange,
        handleSubmit,
        loading: formState.loading || submitting,
        submitting,
        error,
        resetForm,
        setFieldValue,
        setFieldsValue,
    };
}

function cloneFormState<TFormData>(state: FormState<TFormData>): FormState<TFormData> {
    return {
        ...state,
        data: { ...state.data },
        errors: { ...state.errors },
        touched: new Set(state.touched),
    };
}

function extractInputValue(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    const target = event.target;

    if ('type' in target && target.type === 'checkbox') {
        return (target as HTMLInputElement).checked;
    }

    if ('type' in target && target.type === 'number') {
        const input = target as HTMLInputElement;
        const { valueAsNumber, value } = input;
        return value === '' || Number.isNaN(valueAsNumber) ? undefined : valueAsNumber;
    }

    return target.value;
}
